import { getScheduledWhen, getTemplateTaskId } from './taskUtils';
import type { WithId } from '@medplum/core';
import type { Task } from '@medplum/fhirtypes';

/** A row to render on the day view: either an ephemeral slot for a recurring template
 *  (carries `date` + `slotCode`, and `completedOccurrence` when the patient marked it
 *  done) or a one-off task (no `date`).
 *
 *  Structurally identical to `DayViewTask` in `@liza/services`; the canonical shape lives
 *  here so both `@liza/ui` (patient-web / patient-admin-web) and patient-native share one
 *  definition without a services dependency. Each app's tRPC `getDayTasks` return shape is
 *  assignable to this. */
export interface DayItem {
  task: WithId<Task>;
  date?: string;
  slotCode?: string | null;
  completedOccurrence?: WithId<Task>;
  /** Weekly-quota progress, present only on days-per-week template rows:
   *  distinct days completed in the row's Sun–Sat week vs the target N. */
  weeklyProgress?: { completed: number; total: number };
}

/** A `DayItem` is a slot when it carries a non-empty `date`. Predicate form so callers
 *  don't need `item.date!` to read slot coords inside the narrowed branch. */
export function isSlotItem(item: DayItem): item is DayItem & { date: string } {
  return !!item.date;
}

/** Slot-shaped items are complete when a `completedOccurrence` row exists for the slot;
 *  one-offs reflect the task's own status. */
export function isItemCompleted(item: DayItem): boolean {
  if (isSlotItem(item)) return item.completedOccurrence !== undefined;
  return item.task.status === 'completed';
}

/** Stable, unique identifier for a `DayItem`. For slots, includes the date and
 *  `slotCode` so the same template appearing at multiple time slots produces
 *  distinct keys (e.g. MORN vs EVE). For one-offs, the task id alone is unique. */
export function dayItemKey(item: DayItem): string {
  if (isSlotItem(item)) return `${item.task.id}::${item.date}::${item.slotCode ?? ''}`;
  return item.task.id;
}

/** Wrap an arbitrary task in a `DayItem` so toggle dispatch can be reused. Tasks with
 *  a `partOf` template reference become slot-shaped items where `item.task` is a
 *  synthetic template stub (real template id, `intent='plan'`); the original task is
 *  preserved as `completedOccurrence` so the uncomplete path can patch it by id. */
export function toDayItem(task: WithId<Task>): DayItem {
  const templateId = getTemplateTaskId(task);
  if (!templateId) return { task };

  const date = task.executionPeriod?.start?.substring(0, 10);
  const slotCode = getScheduledWhen(task) ?? null;
  return {
    task: { ...task, id: templateId, intent: 'plan' },
    ...(date ? { date } : {}),
    slotCode,
    completedOccurrence: task,
  };
}
